import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledModalOverlay = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'fixed',
  flexDirection: 'column',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(40, 40, 40, 0.6)',
  flexGrow: 0,
  flexShrink: 1,
  zIndex: 10000,
});

const StyledModalBody = styled('div')({
  padding: '20px',
  minHeight: '100px',
  minWidth: '300px',
  display: 'flex',
  flexGrow: 0,
  backgroundColor: '#fff',
  flexDirection: 'column',
  position: 'relative',
  boxShadow: '0 0 20px 0 #444',
  borderRadius: '10px',
});

const StyledModalCloseButton = styled(Button)(({ theme }) => ({
  border: 0,
  position: 'absolute',
  right: '20px',
  borderRadius: '20px',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  width: '30px',
  minWidth: '30px',
  height: '30px',
  textAlign: 'center',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#ddd',
    color: theme.palette.text.secondary,
  },
  '&:not(:hover)': {
    backgroundColor: '#eee',
    color: theme.palette.text.secondary,
  },
})) as typeof Button;

const StyledModalContent = styled('div')({
  paddingTop: '20px',
});

const PortalImpl = ({
  onClose,
  children,
  title,
  closeOnClickOutside,
}: {
  children: ReactNode;
  // eslint-disable-next-line react/boolean-prop-naming
  closeOnClickOutside: boolean;
  onClose: () => void;
  title: string;
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (modalRef.current !== null) {
      modalRef.current.focus();
    }
  }, []);

  useEffect(() => {
    let modalOverlayElement: HTMLElement | null = null;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    const clickOutsideHandler = (event: MouseEvent) => {
      const target = event.target;
      if (modalRef.current !== null && !modalRef.current.contains(target as Node) && closeOnClickOutside) {
        onClose();
      }
    };
    const modelElement = modalRef.current;
    if (modelElement !== null) {
      modalOverlayElement = modelElement.parentElement;
      if (modalOverlayElement !== null) {
        modalOverlayElement.addEventListener('click', clickOutsideHandler);
      }
    }

    window.addEventListener('keydown', handler);

    return () => {
      window.removeEventListener('keydown', handler);
      if (modalOverlayElement !== null) {
        modalOverlayElement?.removeEventListener('click', clickOutsideHandler);
      }
    };
  }, [closeOnClickOutside, onClose]);

  return (
    <StyledModalOverlay role="dialog">
      <StyledModalBody tabIndex={-1} ref={modalRef}>
        <Typography component="h5" variant="h5">
          {title}
        </Typography>
        <StyledModalCloseButton onClick={onClose}>X</StyledModalCloseButton>
        <StyledModalContent className="Modal__content">{children}</StyledModalContent>
      </StyledModalBody>
    </StyledModalOverlay>
  );
};

export const LexicalModal = ({
  onClose,
  children,
  title,
  closeOnClickOutside = false,
}: {
  children: ReactNode;
  closeOnClickOutside?: boolean;
  onClose: () => void;
  title: string;
}): JSX.Element => {
  return createPortal(
    <PortalImpl onClose={onClose} title={title} closeOnClickOutside={closeOnClickOutside}>
      {children}
    </PortalImpl>,
    document.body,
  );
};
